import { c as _c } from "react/compiler-runtime";
import React, { useLayoutEffect, useState } from 'react';
import { fromEntries } from 'remeda';
import { deepEqual } from 'shared/collection';
import { Rnge } from 'shared/range';
import { ChildrenProps, StyleProps, useElementSize } from 'shared/ui';
import { twMerge } from 'tailwind-merge';
import { getFlattenedChildren } from './reactNode';
export const Masonry = ({
  children,
  colWidthBasis = 400,
  maxColWidth,
  className,
  classNames,
  showDebug
}: {
  colWidthBasis?: number;
  maxColWidth?: string | number;
  className?: string;
  classNames?: {
    root?: string;
    column?: string;
  };
  showDebug?: boolean;
} & ChildrenProps) => {
  const {
    ref,
    width
  } = useElementSize();
  const items = getFlattenedChildren(children);
  const [itemsColState, setItemsCol] = useState<Record<string | number, {
    col: number;
    height: number;
  }>>();
  const availableColumns = width == 0 ? undefined : Math.max(Math.floor(width / colWidthBasis), 1); // saves a render doing it int he same effect
  useLayoutEffect(() => {
    if (!ref.current || !availableColumns) return;
    const columnHeights = Array(availableColumns).fill(0);
    const itemEls = Array.from(ref.current.querySelectorAll<HTMLDivElement>('.masonry-column > *'));
    const itemToCol = fromEntries(itemEls.filter(itemEl => !!itemEl.getAttribute('data-item-key')).map(itemEl_0 => {
      const itemKey = itemEl_0.getAttribute('data-item-key')!;
      const key = isNaN(Number(itemKey)) ? itemKey : Number(itemKey);
      const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
      const height = itemEl_0.clientHeight;
      columnHeights[minHeightIndex] += height;
      return [key, {
        col: minHeightIndex,
        height
      }] as const;
    }));
    if (!deepEqual(itemToCol, itemsColState)) setItemsCol(itemToCol);
  }, [ref.current, items.map(c => c.key).join('|'), availableColumns]);
  const availColumnsDisplay = availableColumns ?? 1;
  const columnsItems = Rnge.createSteps([0, availColumnsDisplay]).map(col => ({
    column: col,
    items: items.filter((item, itemIdx) => {
      const itemKey_0 = item.key || itemIdx;
      const colIndex = itemsColState?.[itemKey_0]?.col ?? itemIdx % availColumnsDisplay;
      return colIndex == col;
    })
  }));
  return <div className='space-y-4'>
      <div ref={ref} className={twMerge(`masonry-root`, columnsItems.length > 1 ? 'flex justify-start gap-2' : 'w-full', className, classNames?.root)}>
        {columnsItems.map(({
        column,
        items: items_0
      }) => <div key={column} style={{
        maxWidth: maxColWidth
      }} className={twMerge(`masonry-column grow-1 flex flex-col gap-2 flex-1`, classNames?.column)}>
            {items_0.map(item_0 => {
          const itemKey_1 = item_0.key;
          const itemState = itemKey_1 ? itemsColState?.[itemKey_1] : undefined;
          return <div key={item_0.key} className='relative' data-item-key={itemKey_1}>
                  {item_0}
                  {showDebug && <div className='absolute top-1 right-1 bg-black/50 text-white text-xs px-1 py-0.5 rounded z-10'>
                      {itemKey_1} {itemState ? `→ col${itemState.col} (${itemState.height}px)` : '→ calculating...'}
                    </div>}
                </div>;
        })}
          </div>)}
      </div>
    </div>;
};

/** Masonry where the items are placed in a grid. Purely css so performs much better. Also some things look neater this way */
export const MasonryGrid = t0 => {
  const $ = _c(8);
  const {
    children,
    colWidth: t1,
    className
  } = t0;
  const colWidth = t1 === undefined ? "20em" : t1;
  let t2;
  if ($[0] !== className) {
    t2 = twMerge("grid gap-1", className);
    $[0] = className;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const t3 = `repeat(auto-fit, minmax(min(100%,${colWidth}), 1fr))`;
  let t4;
  if ($[2] !== t3) {
    t4 = {
      gridTemplateColumns: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== children || $[5] !== t2 || $[6] !== t4) {
    t5 = <div className={t2} style={t4}>{children}</div>;
    $[4] = children;
    $[5] = t2;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  return t5;
};